import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { formatMoney } from '@/lib/currencyFormat';
import { isDeductionLineItem, isEarningLineItem } from './totals';
import type { PayslipWithLineItems, PayslipLineItem, PayslipDetailsResponse } from './types';

interface PdfGeneratorOptions {
  currencyCode: string;
}

function fmtCurrency(amount: number, currencyCode: string): string {
  return formatMoney(amount, currencyCode);
}

function fmtDate(dateStr: string, pattern = 'MMM dd, yyyy'): string {
  // Date-only values are calendar dates; parse them as local to avoid a
  // one-day shift in negative-UTC offsets.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = isDateOnly
    ? new Date(Number(dateStr.slice(0, 4)), Number(dateStr.slice(5, 7)) - 1, Number(dateStr.slice(8, 10)))
    : new Date(dateStr);
  return format(d, pattern);
}

export async function generatePayslipPdf(
  details: PayslipDetailsResponse,
  lineItems: PayslipLineItem[] | undefined,
  options: PdfGeneratorOptions
): Promise<Blob> {
  const { payslip, previous_payslip, year_to_date_summary } = details;
  const currencyCode = (payslip as any).currency_code || options.currencyCode;
  const fmt = (n: number) => fmtCurrency(n, currencyCode);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  // ── Header ──
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Payslip', margin, y + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, pageWidth - margin, y + 7, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  y += 14;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Pay Period Info ──
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Pay Period', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`${fmtDate(payslip.start_date)} – ${fmtDate(payslip.end_date)}`, margin + 30, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Issue Date', pageWidth / 2, y);
  doc.setFont('helvetica', 'normal');
  doc.text(fmtDate(payslip.created_at), pageWidth / 2 + 28, y);

  y += 12;

  // ── Summary Box ──
  const grossEarnings = payslip.calculated_totals?.gross_earnings || Number(payslip.gross_pay || 0);
  const totalDeductions = payslip.calculated_totals?.total_deductions || 0;
  const netPay = payslip.calculated_totals?.net_pay || Number(payslip.net_pay || 0);
  const totalHours = Number(payslip.total_hours || 0);

  const boxY = y;
  const boxH = 28;
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(margin, boxY, pageWidth - margin * 2, boxH, 2, 2, 'F');

  const colW = (pageWidth - margin * 2) / 4;
  const summaryItems = [
    { label: 'Gross Pay', value: fmt(grossEarnings), color: [22, 163, 74] as [number, number, number] },
    { label: 'Deductions', value: fmt(totalDeductions), color: [220, 38, 38] as [number, number, number] },
    { label: 'Net Pay', value: fmt(netPay), color: [37, 99, 235] as [number, number, number] },
    { label: 'Total Hours', value: totalHours.toFixed(1), color: [0, 0, 0] as [number, number, number] },
  ];

  summaryItems.forEach((item, i) => {
    const x = margin + colW * i + colW / 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(item.label, x, boxY + 10, { align: 'center' });

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...item.color);
    doc.text(item.value, x, boxY + 20, { align: 'center' });
  });

  doc.setTextColor(0, 0, 0);
  y = boxY + boxH + 10;

  // ── Line Items Table ──
  const items = lineItems && lineItems.length > 0 ? lineItems : payslip.line_items;
  if (items && items.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Earnings & Deductions', margin, y);
    y += 6;

    const earnings = items.filter(isEarningLineItem);
    const deductions = items.filter(isDeductionLineItem);

    const tableRows: (string | number)[][] = [];

    if (earnings.length > 0) {
      tableRows.push([{ content: 'EARNINGS', colSpan: 3, styles: { fillColor: [232, 245, 233], fontStyle: 'bold', fontSize: 8 } } as any]);
      earnings.forEach(item => {
        tableRows.push([
          item.description || 'Earnings',
          item.category || '',
          fmt(Number(item.gross_amount || item.base_amount || 0)),
        ]);
      });
    }

    if (deductions.length > 0) {
      tableRows.push([{ content: 'DEDUCTIONS & TAXES', colSpan: 3, styles: { fillColor: [254, 226, 226], fontStyle: 'bold', fontSize: 8 } } as any]);
      deductions.forEach(item => {
        tableRows.push([
          item.description || 'Deduction',
          item.category || '',
          fmt(Number(item.gross_amount || item.base_amount || 0)),
        ]);
      });
    }

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Category', 'Amount']],
      body: tableRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 35 },
        2: { cellWidth: 35, halign: 'right' },
      },
      theme: 'grid',
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  } else {
    // Legacy payslip — no line items
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text('Detailed line items are not available for this payslip.', margin, y);
    doc.setTextColor(0, 0, 0);
    y += 10;
  }

  // ── Tax Breakdown ──
  const taxBreakdown = payslip.calculated_totals?.tax_breakdown;
  if (taxBreakdown && Object.keys(taxBreakdown).length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Tax Breakdown', margin, y);
    y += 6;

    const taxRows = Object.entries(taxBreakdown).map(([key, amount]) => [
      key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      fmt(Number(amount)),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Tax Type', 'Amount']],
      body: taxRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right' },
      },
      theme: 'grid',
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // ── Year-to-Date Summary ──
  if (year_to_date_summary && year_to_date_summary.periods_count > 1) {
    // Check if we need a new page
    if (y > doc.internal.pageSize.getHeight() - 50) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Year-to-Date Summary', margin, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['YTD Gross Earnings', fmt(year_to_date_summary.total_gross)],
        ['YTD Net Pay', fmt(year_to_date_summary.total_net)],
        ['YTD Total Hours', year_to_date_summary.total_hours.toFixed(1)],
        ['Pay Periods', String(year_to_date_summary.periods_count)],
      ],
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right' },
      },
      theme: 'grid',
    });
  }

  // ── Footer ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 160);
    const pageH = doc.internal.pageSize.getHeight();
    doc.text('Confidential — This document is intended for the named recipient only.', margin, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageH - 8, { align: 'right' });
  }

  return doc.output('blob');
}
